import React from 'react';
import { ReceiptText } from 'lucide-react';
import InlineEmpty from '../InlineEmpty/InlineEmpty';
import StatusBadge from '../StatusBadge/StatusBadge';
import { formatDate } from './time';
import { formatMoney } from '../../utils/datetime';
import { useI18n } from '../../i18n/I18nContext';
import './Student360.css';

const ORDER_TONE = {
  COMPLETED: 'success',
  PENDING: 'warning',
  PROCESSING: 'warning',
  FAILED: 'danger',
  CANCELLED: 'danger',
  REFUNDED: 'info',
};

/**
 * "299 000 so'm". The amount is a decimal string next to its currency code. A zero amount stays
 * "0 so'm" here (formatMoney would call it "Bepul"): it is a line of an order, not a price tag.
 */
function amountText(order, lang) {
  const value = Number(order.amount);
  if (!Number.isFinite(value)) return [String(order.amount ?? ''), order.currency].filter(Boolean).join(' ');
  return formatMoney(value, order.currency, { lang, free: false });
}

/**
 * The orders facet (money): only the viewer kinds the server gives the MN group see it. Amounts
 * arrive as decimal strings next to their currency. A card of the page's right rail.
 */
function OrdersSection({ orders }) {
  const { t, lang } = useI18n();
  const items = orders.items || [];

  return (
    <section className="section-card s360-rail-card" aria-labelledby="s360-orders-title">
      <div className="section-head">
        <h2 id="s360-orders-title" className="section-head__title">{t('student360.orders.title')}</h2>
        <span className="ui-count">{orders.total ?? items.length}</span>
      </div>
      {items.length === 0 ? (
        <InlineEmpty icon={<ReceiptText />} title={t('student360.orders.none')} />
      ) : (
        <ul className="s360-list">
          {items.map((order) => (
            <li key={order.id} className="s360-item">
              <div className="s360-item__main">
                <span className="s360-item__title" title={order.courseTitle}>{order.courseTitle}</span>
                <span className="s360-item__sub">
                  {t(`student360.orders.provider.${order.provider}`)}
                  {' · '}
                  {formatDate(order.paidAt || order.createdAt, lang)}
                </span>
              </div>
              <div className="s360-item__side">
                <strong className="s360-amount">{amountText(order, lang)}</strong>
                <StatusBadge tone={ORDER_TONE[order.status] || 'neutral'}>
                  {t(`student360.orders.status.${order.status}`)}
                </StatusBadge>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export default OrdersSection;
